"use client";

import type { CategorizedAgentDemosBlock } from "@mw/shared/payload-types";
import { appUrls } from "@/constants";
import {
  ChatPlayer,
  type ChatPlayerPlayback,
  type ChatPlayerProps,
  type ChatPlayerSource,
} from "@ui/components/core/ChatPlayer";
import { HairlineCardBorder } from "@ui/components/core/HairlineCardBorder";
import { Link } from "@ui/components/core/Link";
import { Orb } from "@ui/components/core/Orb/Orb";
import { TabList } from "@ui/components/core/TabList";
import { pxToRem } from "@ui/lib/utils";
import { formatLink } from "@utilities/payload";
import type { RenderedBlock } from "@utilities/types";
import { clsx } from "clsx";
import { AnimatePresence, useReducedMotion } from "motion/react";
import { useTranslations } from "next-intl";
import { useMemo, useState } from "react";
import { Tab, TabPanel, Tabs, TabList as AriaTabList, type Key } from "react-aria-components";
import * as m from "motion/react-m";
import { createChatPlayerSourceFromCmsChatScenario } from "./chatScenario";
import {
  AudioDemoPreview as SharedAudioDemoPreview,
  type PreviewAudioController,
  usePreviewAudioController,
} from "@ui/components/demos/AudioDemoPreview/AudioDemoPreview";

type BlockCategorizedAgentDemosProps = RenderedBlock<CategorizedAgentDemosBlock>;
type DemoCategoryData = NonNullable<CategorizedAgentDemosBlock["categories"]>[number];
type DemoData = NonNullable<DemoCategoryData["demos"]>[number];

type DemoPreviewProps = {
  demo: DemoData;
  previewAudio: PreviewAudioController;
};

const CHAT_SCENARIO_PLAYBACK = {
  type: "auto",
  active: true,
  loop: true,
  loopResetMs: 300,
} satisfies ChatPlayerPlayback;
const AUDIO_ASSISTANT_MESSAGE_VARIANTS = {
  variant: "frostedGray",
  maxWidth: "compact",
  tail: "none",
} satisfies ChatPlayerProps["assistantMessageVariants"];
const AUDIO_USER_MESSAGE_VARIANTS = {
  variant: "elevated",
  tail: "left",
} satisfies ChatPlayerProps["userMessageVariants"];
const SCENARIO_ASSISTANT_MESSAGE_VARIANTS = {
  variant: "elevated",
  maxWidth: "compact",
  tail: "left",
} satisfies ChatPlayerProps["assistantMessageVariants"];
const SCENARIO_USER_MESSAGE_VARIANTS = {
  variant: "frostedGray",
  tail: "right",
} satisfies ChatPlayerProps["userMessageVariants"];

function getAudioPreviewConfig(demo: DemoData) {
  return demo.config.audioPreview;
}

function getChatScenarioConfig(demo: DemoData) {
  return demo.config.chatScenario;
}

function getDemoOrbTexture(demo: DemoData) {
  switch (demo.config.template) {
    case "chatScenario":
      return getChatScenarioConfig(demo)?.orbTexture ?? "agents-3";
    case "audioPreview":
    default:
      return getAudioPreviewConfig(demo)?.orbTexture ?? "agents-3";
  }
}

function DemoChatPlayer({
  animateInitialSnapshot = false,
  assistantMessageVariants = AUDIO_ASSISTANT_MESSAGE_VARIANTS,
  playback,
  source,
  userMessageVariants = AUDIO_USER_MESSAGE_VARIANTS,
}: {
  animateInitialSnapshot?: ChatPlayerProps["animateInitialSnapshot"];
  assistantMessageVariants?: ChatPlayerProps["assistantMessageVariants"];
  playback: ChatPlayerPlayback;
  source: ChatPlayerSource;
  userMessageVariants?: ChatPlayerProps["userMessageVariants"];
}) {
  return (
    <div className='tw-relative tw-h-[90%] md:tw-h-80 tw-w-full'>
      <ChatPlayer
        source={source}
        playback={playback}
        animateInitialSnapshot={animateInitialSnapshot}
        assistantMessageVariants={assistantMessageVariants}
        userMessageVariants={userMessageVariants}
        className='tw-size-full'
        anchor='bottom'
      />
      <div
        aria-hidden='true'
        className='tw-pointer-events-none tw-absolute tw-inset-x-0 tw-top-0 tw-z-10 tw-h-16 tw-bg-gradient-to-b tw-from-cream tw-from-30% tw-to-transparent'
      />
    </div>
  );
}

function AudioDemoPreview({ demo, previewAudio }: DemoPreviewProps) {
  const audioPreviewConfig = getAudioPreviewConfig(demo);

  return (
    <SharedAudioDemoPreview
      audio={audioPreviewConfig?.audio}
      chatTranscript={audioPreviewConfig?.sttResponse}
      orbTexture={getDemoOrbTexture(demo)}
      previewAudio={previewAudio}
      speakerRoles={audioPreviewConfig?.speakerRoles}
    />
  );
}

function ChatScenarioDemoPreview({ demo }: { demo: DemoData }) {
  const chatScenarioConfig = getChatScenarioConfig(demo);
  const chatSource = useMemo(() => createChatPlayerSourceFromCmsChatScenario(chatScenarioConfig), [chatScenarioConfig]);

  if (!chatSource) {
    return null;
  }

  return (
    <div className='tw-relative tw-h-full tw-min-h-0 tw-w-full tw-max-w-72 sm:tw-max-w-sm md:tw-max-w-md'>
      <DemoChatPlayer
        source={chatSource}
        playback={CHAT_SCENARIO_PLAYBACK}
        animateInitialSnapshot
        assistantMessageVariants={SCENARIO_ASSISTANT_MESSAGE_VARIANTS}
        userMessageVariants={SCENARIO_USER_MESSAGE_VARIANTS}
      />
    </div>
  );
}

function DemoPreview(props: DemoPreviewProps) {
  switch (props.demo.config.template) {
    case "chatScenario":
      return <ChatScenarioDemoPreview demo={props.demo} />;
    case "audioPreview":
    default:
      return <AudioDemoPreview {...props} />;
  }
}

function DemoCategory({
  category,
  previewAudio,
  reducedMotion,
}: {
  category: DemoCategoryData;
  previewAudio: PreviewAudioController;
  reducedMotion: boolean;
}) {
  const t = useTranslations("ClientKeys.BlockCategorizedAgentDemos");
  const [selectedDemo, setSelectedDemo] = useState<Key>(0);
  const selectedDemoData = category.demos?.[Number(selectedDemo)];
  const selectedDemoLink = formatLink(selectedDemoData?.ctaLink) ?? { href: appUrls.convAI, label: t("use_agent") };

  const handleSelectedDemoChange = (key: Key) => {
    if (key === selectedDemo) return;

    previewAudio.resetForTabChange();
    setSelectedDemo(key);
  };

  return (
    <m.div {...getTabContentMotionProps(reducedMotion)}>
      <div className='tw-flex tw-justify-between'>
        <div className='tw-pl-6 tw-pt-6'>
          <p className='tw-type-base tw-text-black tw-text-pretty'>{category.title}</p>
          <p className='tw-type-xs tw-text-pretty tw-text-gray-500 tw-mt-3'>{category.description}</p>
        </div>
        <div className='tw-flex tw-gap-x-2 tw-p-2'>
          <Link {...selectedDemoLink} asButton buttonVariants={{ size: "md", variant: "elevated" }} />
        </div>
      </div>
      <Tabs selectedKey={selectedDemo} onSelectionChange={handleSelectedDemoChange}>
        <AnimatePresence mode='wait' initial={false}>
          {category.demos?.map((demo, demoIndex) =>
            demoIndex === selectedDemo ? (
              <TabPanel
                key={demoIndex}
                id={demoIndex}
                style={{ height: pxToRem(400) }}
                shouldForceMount
                className='tw-flex tw-px-6'
              >
                <m.div
                  {...getTabContentMotionProps(reducedMotion)}
                  className='tw-relative tw-flex tw-h-full tw-w-full tw-items-center tw-justify-center'
                >
                  <DemoPreview demo={demo} previewAudio={previewAudio} />
                </m.div>
              </TabPanel>
            ) : null,
          )}
        </AnimatePresence>
        <div className='tw-relative'>
          <AriaTabList
            aria-label={t("demo_tabs_label", { category_title: category.title })}
            onClickCapture={previewAudio.mute}
            className='tw-flex tw-pt-4 -tw-mt-4 tw-pb-6 tw-px-6 sm:tw-pb-8 sm:tw-px-8 md:tw-pb-12 md:tw-px-12 tw-gap-x-14 tw-whitespace-nowrap tw-overflow-x-auto tw-scrollbar-none'
          >
            {category.demos?.map((demo, demoIndex, demos) => (
              <Tab
                key={demoIndex}
                id={demoIndex}
                className={clsx(
                  "tw-flex tw-gap-x-3 tw-cursor-pointer tw-outline-none focus-visible:tw-outline-focus tw-rounded-md",
                  demoIndex === 0 && "tw-ml-auto",
                  demoIndex === demos.length - 1 && "tw-mr-auto",
                )}
              >
                {({ isSelected }) => (
                  <>
                    <div className='tw-relative tw-flex-none tw-size-9 tw-rounded-full tw-overflow-hidden tw-bg-gray-200'>
                      <AnimatePresence initial={false}>
                        {isSelected && (
                          <m.div
                            className='tw-absolute tw-inset-0'
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 0.2, ease: "easeOut" }}
                          >
                            <Orb texture={getDemoOrbTexture(demo)} animated={false} image={{ sizes: pxToRem(36) }} />
                          </m.div>
                        )}
                      </AnimatePresence>
                    </div>
                    <div className='tw-my-auto'>
                      <div
                        className={clsx(
                          "tw-type-base tw-transition-colors",
                          isSelected ? "tw-text-black" : "tw-text-gray-400",
                        )}
                      >
                        {demo.title}
                      </div>
                      <div
                        className={clsx(
                          "tw-mt-2.5 tw-type-xs tw-transition-colors",
                          isSelected ? "tw-text-gray-500" : "tw-text-gray-400",
                        )}
                      >
                        {demo.description}
                      </div>
                    </div>
                  </>
                )}
              </Tab>
            ))}
          </AriaTabList>
          <div
            aria-hidden='true'
            className='tw-pointer-events-none tw-absolute tw-inset-y-0 tw-left-0 tw-z-10 tw-w-6 tw-bg-gradient-to-r tw-from-cream tw-from-5% tw-to-transparent sm:tw-w-8 md:tw-w-12'
          />
          <div
            aria-hidden='true'
            className='tw-pointer-events-none tw-absolute tw-inset-y-0 tw-right-0 tw-z-10 tw-w-6 tw-bg-gradient-to-l tw-from-cream tw-from-5% tw-to-transparent sm:tw-w-8 md:tw-w-12'
          />
        </div>
      </Tabs>
    </m.div>
  );
}

function getTabContentMotionProps(reducedMotion: boolean) {
  if (reducedMotion) {
    return {
      initial: false as const,
      animate: { opacity: 1 },
      exit: { opacity: 1 },
      transition: { duration: 0 },
    };
  }

  return {
    initial: { opacity: 0 },
    animate: { opacity: 1 },
    exit: { opacity: 0 },
  };
}

function BlockCategorizedAgentDemosContent({ categories }: BlockCategorizedAgentDemosProps) {
  const t = useTranslations("ClientKeys.BlockCategorizedAgentDemos");
  const prefersReducedMotion = useReducedMotion();
  const reducedMotion = Boolean(prefersReducedMotion);
  const [selectedCategory, setSelectedCategory] = useState<Key>("0");
  const previewAudio = usePreviewAudioController();

  const handleSelectedCategoryChange = (key: Key) => {
    if (key === selectedCategory) return;

    previewAudio.resetForTabChange();
    setSelectedCategory(key);
  };

  if (!categories?.length) {
    return null;
  }

  return (
    <Tabs selectedKey={selectedCategory} onSelectionChange={handleSelectedCategoryChange} className='-tw-mx-full-bleed'>
      <div
        style={
          {
            paddingLeft: "var(--section-px-default)",
            paddingRight: "var(--section-px-default)",
            "--full-bleed": "var(--section-px-default)",
          } as React.CSSProperties
        }
      >
        <TabList
          aria-label={t("category_tabs_label")}
          onClickCapture={previewAudio.mute}
          items={categories.map((cat, catIndex) => ({ id: catIndex.toString(), title: cat.title }))}
        />
      </div>
      <div
        className='tw-mt-5 tw-relative tw-overflow-hidden tw-bg-cream tw-rounded-3xl'
        style={{
          marginLeft: "var(--section-px-sm)",
          marginRight: "var(--section-px-sm)",
        }}
      >
        <HairlineCardBorder roundedClassName='tw-rounded-3xl' />
        <AnimatePresence mode='wait' initial={false}>
          {categories.map((cat, catIndex) =>
            catIndex.toString() === selectedCategory ? (
              <TabPanel id={catIndex.toString()} key={catIndex} shouldForceMount>
                <DemoCategory category={cat} previewAudio={previewAudio} reducedMotion={reducedMotion} />
              </TabPanel>
            ) : null,
          )}
        </AnimatePresence>
      </div>
    </Tabs>
  );
}

export function BlockCategorizedAgentDemos(props: BlockCategorizedAgentDemosProps) {
  if (!props.categories?.length) {
    return null;
  }

  return <BlockCategorizedAgentDemosContent {...props} />;
}
